import type {
  IRfidCredentialRepository,
  PaginatedResponse,
  RfidQueryParams,
} from '@yawi/domain';
import { buildQueryString, DEFAULT_PAGE, DEFAULT_PAGE_SIZE } from '@yawi/domain';
import type {
  CreateRfidCredentialDTO,
  RfidCardType,
  RfidCredential,
  RfidCredentialStatus,
  UpdateRfidCredentialDTO,
} from '@yawi/types';
import { getApiClient } from '../../http/apiClient';

// API Response types
interface ApiRfidCredentialResponse {
  id: string;
  memberId: string;
  memberName?: string | null;
  tagId: string;
  label: string;
  cardType: 'PROXIMITY' | 'MIFARE' | 'HID' | 'EM4100' | 'OTHER';
  status: 'ACTIVE' | 'INACTIVE' | 'LOST' | 'REVOKED';
  facilityCode?: string | null;
  notes?: string | null;
  lastUsedAt?: string | null;
  createdAt: string;
  updatedAt: string;
  member: {
    id: string;
    displayName: string;
  };
}

// Map API response to domain type
const mapApiRfidToCredential = (apiRfid: ApiRfidCredentialResponse): RfidCredential => ({
  id: apiRfid.id,
  memberId: apiRfid.memberId,
  memberName: apiRfid.memberName || undefined,
  tagId: apiRfid.tagId,
  label: apiRfid.label,
  cardType: apiRfid.cardType as RfidCardType,
  status: apiRfid.status as RfidCredentialStatus,
  facilityCode: apiRfid.facilityCode || undefined,
  notes: apiRfid.notes || undefined,
  lastUsedAt: apiRfid.lastUsedAt || undefined,
  createdAt: apiRfid.createdAt,
  updatedAt: apiRfid.updatedAt,
  member: apiRfid.member
});

export class ApiRfidCredentialRepository implements IRfidCredentialRepository {
  // Admin: Get all credentials with pagination and filters
  async getCredentials(params?: RfidQueryParams): Promise<PaginatedResponse<RfidCredential>> {
    const client = getApiClient();
    const queryString = buildQueryString({
      page: params?.page ?? DEFAULT_PAGE,
      limit: params?.limit ?? DEFAULT_PAGE_SIZE,
      search: params?.search,
      sortBy: params?.sortBy,
      sortOrder: params?.sortOrder,
      memberId: params?.memberId,
      status: params?.status,
    });
    const response = await client.get<PaginatedResponse<ApiRfidCredentialResponse>>(
      `v1/rfid-credentials?${queryString}`
    );
    return {
      ...response,
      data: response.data.map(mapApiRfidToCredential),
    };
  }

  // Admin: Get credential by ID
  async getCredentialById(id: string): Promise<RfidCredential | null> {
    const client = getApiClient();
    try {
      const apiRfid = await client.get<ApiRfidCredentialResponse>(`v1/rfid-credentials/${id}`);
      return mapApiRfidToCredential(apiRfid);
    } catch {
      return null;
    }
  }

  // Admin: Get credentials by member
  async getCredentialsByMember(memberId: string): Promise<RfidCredential[]> {
    const client = getApiClient();
    const response = await client.get<ApiRfidCredentialResponse[]>(
      `v1/rfid-credentials/member/${memberId}`
    );
    return response.map(mapApiRfidToCredential);
  }

  // Admin: Create new credential
  async createCredential(dto: CreateRfidCredentialDTO): Promise<RfidCredential> {
    const client = getApiClient();
    const apiRfid = await client.post<ApiRfidCredentialResponse>('v1/rfid-credentials', dto);
    return mapApiRfidToCredential(apiRfid);
  }

  // Admin: Update credential
  async updateCredential(id: string, dto: UpdateRfidCredentialDTO): Promise<RfidCredential> {
    const client = getApiClient();
    const apiRfid = await client.put<ApiRfidCredentialResponse>(`v1/rfid-credentials/${id}`, dto);
    return mapApiRfidToCredential(apiRfid);
  }

  // Admin: Update status only
  async updateStatus(id: string, status: RfidCredentialStatus): Promise<RfidCredential> {
    const client = getApiClient();
    const apiRfid = await client.put<ApiRfidCredentialResponse>(
      `v1/rfid-credentials/${id}/status`,
      { status }
    );
    return mapApiRfidToCredential(apiRfid);
  }

  // Admin: Delete credential
  async deleteCredential(id: string): Promise<void> {
    const client = getApiClient();
    await client.delete(`v1/rfid-credentials/${id}`);
  }

  // Resident: Get my credentials
  async getMyCredentials(): Promise<RfidCredential[]> {
    const client = getApiClient();
    const response = await client.get<ApiRfidCredentialResponse[]>('v1/rfid-credentials/me');
    return response.map(mapApiRfidToCredential);
  }

  // Resident: Report card as lost
  async reportLost(id: string): Promise<RfidCredential> {
    const client = getApiClient();
    const apiRfid = await client.put<ApiRfidCredentialResponse>(
      `v1/rfid-credentials/${id}/report-lost`
    );
    return mapApiRfidToCredential(apiRfid);
  }
}
